/**
 * Matches a guardrail capability to the regulation clause it was built from.
 *
 * A guardrail's name is its clause reference, for example "COBS 4.2.1", so the
 * ingested clause with the same reference is the one it came from. The clause
 * carries the title, the plain-language explanation and the wording itself.
 */
import type {
  PaneTypes,
  ProcessedClause,
} from "@financial-advisory/data-access/api-types";

export interface GuardrailClause {
  /**
   * Plain-language title, e.g. "Fair, clear and not misleading communications".
   */
  title: string;
  /**
   * Clause reference as written in the handbook, e.g. "COBS 4.2.1R".
   */
  reference: string;
  /**
   * What the clause requires, in plain language.
   */
  explanation?: string;
  /**
   * The wording exactly as ingested.
   */
  excerpt?: string;
}

const normalise = (reference: string): string =>
  reference.replace(/\s+/g, "").toUpperCase();

const firstNonEmpty = (
  panes: PaneTypes[],
  pick: (pane: PaneTypes) => string | undefined
): string | undefined => {
  for (const pane of panes) {
    const value = pick(pane)?.trim();
    if (value) return value;
  }
  return undefined;
};

/**
 * Builds a lookup from clause reference to the parts of the clause the Guardrails
 * page shows. A reference that appears twice keeps the first clause.
 */
export const buildClauseLookup = (
  clauses: ProcessedClause[]
): Map<string, GuardrailClause> => {
  const lookup = new Map<string, GuardrailClause>();

  for (const clause of clauses) {
    const reference =
      clause.reference?.globalReference || clause.reference?.rawText;
    if (!reference) continue;

    const key = normalise(reference);
    if (lookup.has(key)) continue;

    const panes = clause.panes ?? [];
    lookup.set(key, {
      title: clause.title,
      reference: clause.reference?.rawText || reference,
      explanation: firstNonEmpty(panes, (pane) => pane.explanation),
      excerpt: firstNonEmpty(panes, (pane) => pane.excerpt),
    });
  }

  return lookup;
};

/**
 * Finds the clause a guardrail was built from. Matches on the whole reference
 * first, then on progressively shorter ones, so "COBS 4.2.1" still finds the
 * clause ingested as "COBS 4.2".
 */
export const findClauseForGuardrail = (
  guardrailName: string,
  lookup: Map<string, GuardrailClause>
): GuardrailClause | undefined => {
  const parts = guardrailName.trim().split(".");
  for (let end = parts.length; end > 0; end--) {
    const candidate = lookup.get(normalise(parts.slice(0, end).join(".")));
    if (candidate) return candidate;
  }
  return undefined;
};
